import { NavigationGuardNext, RouteLocationNormalized } from 'vue-router';
import { authService } from '@/features/common/auth/services/authService';
import { logger } from '@/common/services/config';
import { UserRole } from '../types';

/**
 * Simplified authentication and authorization guard
 */
export async function authGuard(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  logger.log('🛡️ AuthGuard: Checking route', to.path);

  // Wait for auth initialization to complete
  await authService.waitForInitialization();
  logger.log('🛡️ AuthGuard: Initialization complete');

  // If authenticated user tries to access login, redirect to home
  if (to.name === 'Login') {
    const isAuth = authService.isAuthenticated();
    if (isAuth) {
      logger.log('🛡️ AuthGuard: User already authenticated, redirecting to home');
      const userRoles = authService.getUserRoles();
      next(getDefaultRouteForUser(userRoles));
      return;
    }
    logger.log('🛡️ AuthGuard: Allowing access to login page');
    next();
    return;
  }

  // Allow access to public routes
  if (!to.meta.requiresRole) {
    logger.log('🛡️ AuthGuard: Public route, allowing access');
    next();
    return;
  }

  // Check authentication
  const isAuth = authService.isAuthenticated();
  logger.log('🛡️ AuthGuard: isAuthenticated =', isAuth);

  if (!isAuth) {
    logger.log('🛡️ AuthGuard: User not authenticated, redirecting to login');
    next({ name: 'Login' });
    return;
  }

  // Check authorization
  const requiredRole = to.meta.requiresRole as UserRole;
  const userRoles = authService.getUserRoles();

  // ADMINISTRATOR has access to COLLABORATEUR routes as well
  const hasRequiredRole =
    userRoles.includes(requiredRole) ||
    (requiredRole === UserRole.COLLABORATEUR && userRoles.includes(UserRole.ADMINISTRATOR));

  if (!hasRequiredRole) {
    logger.log(`User doesn't have required role: ${requiredRole}`);
    next({ name: 'NotAllowed' });
    return;
  }

  // All checks passed
  next();
}

/**
 * Determine appropriate home route based on user roles
 */
export function getDefaultRouteForUser(userRoles: string[]): string {
  logger.log('Determining default route for user with roles:', userRoles);
  if (userRoles.includes(UserRole.ADMINISTRATOR) || userRoles.includes(UserRole.COLLABORATEUR)) {
    return '/employee-home';
  } else if (userRoles.includes(UserRole.STUDENT)) {
    return '/student-home';
  }
  return '/';
}
